import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';

// ── Responsive per-page with manual override ──
function usePerPage() {
  const getDefault = () => window.innerWidth >= 640 ? 5 : 5;
  const [perPage,  setPerPage]  = useState(5);
  const [isManual, setIsManual] = useState(false);

  useEffect(() => {
    setPerPage(getDefault());
    const update = () => { if (!isManual) setPerPage(getDefault()); };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isManual]);

  const change = (n) => { setIsManual(true); setPerPage(n); };
  return { perPage, change };
}

// ── Pagination hook ──
function usePagination(data, perPage) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

// ── Per-page selector ──
function PerPageSelector({ perPage, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-secondary">Rows</span>
      <div className="flex gap-1">
        {[5, 10, 20, 50].map(n => (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
              ${perPage === n
                ? 'bg-primary text-white'
                : 'text-secondary hover:bg-surface-container hover:text-on-surface border border-surface-container-high'}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Pagination bar ──
function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }

  return (
    <div className="flex items-center justify-between mt-3">
      <button
        onClick={() => setPage(p => Math.max(1, p - 1))}
        disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:text-on-surface hover:bg-surface-container
                   disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>
        Prev
      </button>

      <div className="flex items-center gap-1">
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`dots-${i}`} className="px-1 text-xs text-secondary">…</span>
          ) : (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
                ${page === p
                  ? 'bg-primary text-white'
                  : 'text-secondary hover:bg-surface-container hover:text-on-surface'}`}
            >
              {p}
            </button>
          )
        )}
      </div>

      <button
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:text-on-surface hover:bg-surface-container
                   disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        Next
        <span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

export default function PaymentsPanel() {
  const [payments,     setPayments]     = useState([]);
  const [shopPayments, setShopPayments] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('purchases');

  useEffect(() => {
    Promise.all([
      api.get('/api/butcher/payments'),
      api.get('/api/butcher/shop-orders'),
    ])
      .then(([r1, r2]) => {
        setPayments(r1.data);
        setShopPayments(r2.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalSpent   = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalEarned  = shopPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalPending = payments.filter(p => p.status === 'pending')
                               .reduce((s, p) => s + Number(p.amount || 0), 0);

  const statusColor = {
    pending:          'bg-amber-100 text-amber-700',
    confirmed:        'bg-blue-100 text-blue-700',
    completed:        'bg-emerald-100 text-emerald-700',
    cancelled:        'bg-red-100 text-red-700',
    out_for_delivery: 'bg-purple-100 text-purple-700',
  };

  const tabs = [
    { key: 'purchases', label: 'Animal Purchases', shortLabel: 'Purchases', icon: 'payments',   activeColor: 'text-primary' },
    { key: 'earnings',  label: 'Shop Earnings',    shortLabel: 'Earnings',  icon: 'storefront', activeColor: 'text-emerald-600' },
  ];

  const { perPage, change: changePerPage } = usePerPage();
  const purchasesPag = usePagination(payments,     perPage);
  const earningsPag  = usePagination(shopPayments, perPage);

  useEffect(() => {
    purchasesPag.reset();
    earningsPag.reset();
  }, [perPage]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    purchasesPag.reset();
    earningsPag.reset();
  };

  const handlePerPageChange = (n) => {
    changePerPage(n);
    purchasesPag.reset();
    earningsPag.reset();
  };

  return (
    <div className="space-y-4">

      {/* ── Summary cards ── */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Spent',   value: totalSpent,   icon: 'payments',   color: 'text-primary',     bg: 'bg-primary/10' },
          { label: 'Earned',  value: totalEarned,  icon: 'storefront', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Pending', value: totalPending, icon: 'pending',    color: 'text-amber-600',   bg: 'bg-amber-50'   },
        ].map(s => (
          <div key={s.label}
            className="bg-white rounded-2xl p-3 sm:p-5 shadow-sm border border-outline-variant/10 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-secondary">{s.label}</p>
              <span className={`material-symbols-outlined ${s.color} ${s.bg} p-1.5 sm:p-2.5 rounded-lg text-[16px] sm:text-[20px]`}>
                {s.icon}
              </span>
            </div>
            <p className="text-base sm:text-2xl font-black text-on-surface leading-none">
              ₹{Number(s.value).toLocaleString('en-IN')}
            </p>
          </div>
        ))}
      </div>

      {/* ── Toggle ── */}
      <div className="bg-surface-container-low rounded-2xl p-1.5 flex gap-1 shadow-sm border border-outline-variant/10">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 sm:px-4 rounded-xl
                        text-xs sm:text-sm font-bold transition-all duration-200 min-w-0
                        ${activeTab === t.key
                          ? `bg-white shadow ${t.activeColor}`
                          : 'text-secondary hover:text-on-surface'}`}
          >
            <span className="material-symbols-outlined text-[16px] sm:text-[18px] shrink-0">{t.icon}</span>
            <span className="truncate">
              <span className="sm:hidden">{t.shortLabel}</span>
              <span className="hidden sm:inline">{t.label}</span>
            </span>
          </button>
        ))}
      </div>

      {/* ── Animal Purchases ── */}
      {activeTab === 'purchases' && (
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-outline-variant/10">
          <h3 className="font-bold text-base sm:text-lg mb-0.5">Animal Purchases</h3>
          <p className="text-xs text-secondary mb-4">Payments made to livestock sellers for buying animals</p>

          {loading ? (
            <div className="py-10 text-center text-secondary text-sm">Loading...</div>
          ) : payments.length === 0 ? (
            <div className="py-10 text-center">
              <span className="material-symbols-outlined text-4xl text-outline-variant">payments</span>
              <p className="mt-2 text-sm font-medium text-secondary">No purchases yet</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {purchasesPag.slice.map(p => (
                  <div key={p.id} className="rounded-xl border border-surface-container-high p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="text-sm font-bold">{p.animal_name}</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {p.animal_category && (
                            <span className="text-[10px] bg-surface-container px-1.5 py-0.5 rounded text-secondary font-medium">
                              {p.animal_category}
                            </span>
                          )}
                          {p.weight_kg && (
                            <span className="text-[10px] text-secondary">{p.weight_kg} kg</span>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold shrink-0
                                        ${statusColor[p.status] || 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-secondary">
                      <span>{p.seller_name || '—'}{p.seller_phone && ` · ${p.seller_phone}`}</span>
                      <span>{new Date(p.created_at).toLocaleDateString('en-IN')}</span>
                    </div>
                    <p className="text-sm font-bold text-primary">
                      ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-surface-container-high">
                      {['Animal', 'Category', 'Weight', 'Seller', 'Amount', 'Status', 'Date'].map(h => (
                        <th key={h} className="pb-3 text-[10px] font-black uppercase tracking-widest text-secondary">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-container">
                    {purchasesPag.slice.map(p => (
                      <tr key={p.id} className="hover:bg-surface-container-low/40 transition-colors">
                        <td className="py-3 text-sm font-bold">{p.animal_name}</td>
                        <td className="py-3 text-sm text-secondary">{p.animal_category || '—'}</td>
                        <td className="py-3 text-sm text-secondary">{p.weight_kg ? `${p.weight_kg} kg` : '—'}</td>
                        <td className="py-3 text-sm text-secondary">
                          <div>{p.seller_name || '—'}</div>
                          {p.seller_phone && <div className="text-xs text-secondary/60">{p.seller_phone}</div>}
                        </td>
                        <td className="py-3 text-sm font-bold text-primary">
                          ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="py-3">
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold
                                            ${statusColor[p.status] || 'bg-gray-100 text-gray-600'}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="py-3 text-xs text-secondary">
                          {new Date(p.created_at).toLocaleDateString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Footer: per-page + count + pagination ── */}
              <div className="mt-4 pt-4 border-t border-surface-container space-y-3">
                <div className="flex items-center justify-between">
                  <PerPageSelector perPage={perPage} onChange={handlePerPageChange} />
                  <p className="text-xs text-secondary">
                    {((purchasesPag.page - 1) * perPage) + 1}–{Math.min(purchasesPag.page * perPage, payments.length)} of {payments.length}
                  </p>
                </div>
                <PaginationBar
                  page={purchasesPag.page}
                  totalPages={purchasesPag.totalPages}
                  setPage={purchasesPag.setPage}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Shop Earnings ── */}
      {activeTab === 'earnings' && (
        <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-outline-variant/10">
          <h3 className="font-bold text-base sm:text-lg mb-0.5">Meat Shop Earnings</h3>
          <p className="text-xs text-secondary mb-4">Payments received from customers for meat products</p>

          {loading ? (
            <div className="py-10 text-center text-secondary text-sm">Loading...</div>
          ) : shopPayments.length === 0 ? (
            <div className="py-10 text-center">
              <span className="material-symbols-outlined text-4xl text-outline-variant">storefront</span>
              <p className="mt-2 text-sm font-medium text-secondary">No shop earnings yet</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {earningsPag.slice.map(p => (
                  <div key={p.id} className="rounded-xl border border-surface-container-high p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-bold">{p.product_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold shrink-0
                                        ${statusColor[p.status] || 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-secondary">
                      <span>{p.buyer_name || '—'} · {p.quantity} {p.unit || 'qty'}</span>
                      <span>{new Date(p.created_at).toLocaleDateString('en-IN')}</span>
                    </div>
                    <p className="text-sm font-bold text-emerald-600">
                      ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-surface-container-high">
                      {['Product', 'Category', 'Customer', 'Qty', 'Unit Price', 'Amount', 'Status', 'Date'].map(h => (
                        <th key={h} className="pb-3 text-[10px] font-black uppercase tracking-widest text-secondary">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-container">
                    {earningsPag.slice.map(p => (
                      <tr key={p.id} className="hover:bg-surface-container-low/40 transition-colors">
                        <td className="py-3 text-sm font-bold">{p.product_name}</td>
                        <td className="py-3 text-sm text-secondary">{p.product_category || '—'}</td>
                        <td className="py-3 text-sm text-secondary">{p.buyer_name || '—'}</td>
                        <td className="py-3 text-sm text-secondary">{p.quantity} {p.unit || ''}</td>
                        <td className="py-3 text-sm text-secondary">
                          {p.unit_price ? `₹${Number(p.unit_price).toLocaleString('en-IN')}` : '—'}
                        </td>
                        <td className="py-3 text-sm font-bold text-emerald-600">
                          ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="py-3">
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold
                                            ${statusColor[p.status] || 'bg-gray-100 text-gray-600'}`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="py-3 text-xs text-secondary">
                          {new Date(p.created_at).toLocaleDateString('en-IN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Footer: per-page + count + pagination ── */}
              <div className="mt-4 pt-4 border-t border-surface-container space-y-3">
                <div className="flex items-center justify-between">
                  <PerPageSelector perPage={perPage} onChange={handlePerPageChange} />
                  <p className="text-xs text-secondary">
                    {((earningsPag.page - 1) * perPage) + 1}–{Math.min(earningsPag.page * perPage, shopPayments.length)} of {shopPayments.length}
                  </p>
                </div>
                <PaginationBar
                  page={earningsPag.page}
                  totalPages={earningsPag.totalPages}
                  setPage={earningsPag.setPage}
                />
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
}